import { useCanvas } from "@/contexts/CanvasContext";
import { useThreadStore } from "@/store";
import { useCallback, useEffect, useMemo } from "react";
import { generateCanvasTitle } from "../utils/titleExtractor";

/**
 * useCanvasIntegration - Manages canvas auto-open logic and canvas interaction
 *
 * Handles:
 * - Auto-opening canvas when tool calls complete (with conditions)
 * - Generating canvas titles from user queries
 * - Canvas click handling with data extraction
 * - Preventing auto-open for loaded threads
 * - Respecting manually closed canvas state
 *
 * @param {boolean} isUser - Whether this is a user message
 * @param {Object} effectiveMessage - The effective message object
 * @param {Object} effectiveCanvasData - Canvas data from tool calls
 * @param {boolean} effectiveIsStreaming - Whether message is streaming
 * @param {boolean} hasCompletedToolCalls - Whether tool calls are completed
 * @param {Object} previousUserMessage - Previous user message
 * @param {function} handleCanvasClick - Canvas click handler function
 * @returns {Object} Canvas integration utilities
 */
export const useCanvasIntegration = (
  isUser,
  effectiveMessage,
  effectiveCanvasData,
  effectiveIsStreaming,
  hasCompletedToolCalls,
  previousUserMessage,
  handleCanvasClick
) => {
  const { isCanvasOpen, canvasSourceMessageId, isManuallyClosed } = useCanvas();
  const currentMessageId = useThreadStore((state) => state.currentMessageId);

  // Generate canvas title from user query or canvas data
  const canvasTitleFromQuery = useMemo(() => {
    if (!effectiveCanvasData) return null;

    // First, try to get title from canvas data itself
    const titleFromData =
      effectiveCanvasData.name ||
      effectiveCanvasData.title ||
      effectiveCanvasData.canvasData?.name ||
      effectiveCanvasData.canvasData?.title;

    if (titleFromData && titleFromData !== "Canvas") {
      return titleFromData;
    }

    // If no title in data, generate from user query
    if (previousUserMessage?.content) {
      const query =
        typeof previousUserMessage.content === "string"
          ? previousUserMessage.content
          : previousUserMessage.content?.text || "";
      return generateCanvasTitle(query);
    }

    return "Canvas";
  }, [effectiveCanvasData, previousUserMessage]);

  // Check if canvas should auto-open
  const shouldAutoOpenCanvas = useCallback(() => {
    const userMessage = previousUserMessage;

    // Check if canvas mode was explicitly enabled
    const canvasModeWasEnabled =
      userMessage?.metadata?.canvasModeEnabled === true;

    // Check if user message explicitly mentions canvas
    const userMessageContent = userMessage?.content?.toLowerCase() || "";
    const mentionsCanvas =
      userMessageContent.includes("canvas") ||
      userMessageContent.includes("use canvas") ||
      userMessageContent.includes("open canvas") ||
      userMessageContent.includes("create canvas") ||
      userMessageContent.includes("in canvas");

    return canvasModeWasEnabled || mentionsCanvas;
  }, [previousUserMessage]);

  // Auto-open canvas when canvas tool result completes
  useEffect(() => {
    // Only auto-open for assistant messages with canvas data that have completed
    // IMPORTANT: Only auto-open for NEWLY created canvases (currently streaming message),
    // NOT for canvases in loaded threads
    const isNewlyCreatedMessage = effectiveMessage.id === currentMessageId;
    const shouldAutoOpen = shouldAutoOpenCanvas();

    if (
      !isUser &&
      effectiveCanvasData &&
      !effectiveIsStreaming &&
      hasCompletedToolCalls &&
      isNewlyCreatedMessage &&
      shouldAutoOpen
    ) {
      const isAlreadyOpenForThisMessage =
        isCanvasOpen && canvasSourceMessageId === effectiveMessage.id;

      const wasManuallyClosed =
        isManuallyClosed?.(effectiveMessage.id) || false;

      if (!isAlreadyOpenForThisMessage && !wasManuallyClosed) {
        const timeoutId = setTimeout(() => {
          handleCanvasClick(effectiveCanvasData);
        }, 100);

        return () => {
          clearTimeout(timeoutId);
        };
      }
    }
  }, [
    isUser,
    effectiveCanvasData,
    effectiveIsStreaming,
    hasCompletedToolCalls,
    isCanvasOpen,
    effectiveMessage.id,
    canvasSourceMessageId,
    handleCanvasClick,
    isManuallyClosed,
    currentMessageId,
    shouldAutoOpenCanvas,
  ]);

  return {
    canvasTitleFromQuery,
    shouldAutoOpenCanvas,
  };
};
